/**
 * chart-of-accounts-segments.ts — plan kont jako źródło PODPOWIEDZI dla 6 segmentów konta
 * w formatce dekretacji (`DecreeLinesEditor`).
 *
 * Po co osobny moduł, a nie `AccountSearchPicker`: tamten picker wybiera KONTO JAKO CAŁOŚĆ
 * (zwraca `chart_of_accounts.id`) i pochodzi z modelu sprzed uproszczenia dekretacji. Po
 * migracji 0140 wiersz dekretu niesie SZEŚĆ niezależnych segmentów tekstowych, więc
 * podpowiedź musi działać per segment i KASKADOWO: opcje „Pozycji 1" zależą od wpisanej
 * Syntetyki, opcje „Pozycji 2" od Syntetyki + Pozycji 1 itd. Pole zostaje zwykłym inputem
 * (można wpisać cokolwiek, także konto, którego nie ma w planie) — lista jest pomocą, nie
 * bramką; backend też nie waliduje segmentów względem planu kont.
 *
 * Plan kont jest per (firma, ROK OBRACHUNKOWY) od migracji 0141, więc indeks buduje się dla
 * konkretnego roku — stąd selektor roku w formatce dekretacji.
 */

import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { api } from '@/lib/api';

/** Segment oznaczający POZIOM SŁOWNIKOWY w numerze konta (migracja 0154, np. `201-x`). */
const DICTIONARY_SEGMENT = 'x';

/** Rok obrachunkowy planu kont + licznik kont — `GET /chart-of-accounts/years`. */
export interface ChartFiscalYear {
  fiscalYear: string;
  lastImportedAt: string;
  accountCount: number;
}

/** Wiersz `GET /chart-of-accounts?format=flat` — subset pól potrzebny do podpowiedzi. */
interface ChartFlatRow {
  id: string;
  accountNumber: string;
  name: string;
  isPostable: boolean;
  isActive: boolean;
  isBlocked: boolean;
  dictionarySource: 'dictionary' | 'contractors' | 'employees' | null;
  /** Kod lokalnego słownika z pozycjami z ERP (`null` = brak powiązania). */
  dictionaryCode?: string | null;
}

/**
 * POZIOM SŁOWNIKOWY analityki (migracja 0154) — w planie kont zapisany jako segment `x`
 * (`402-x`). Wartością analityki NIE jest tu numer konta, a POZYCJA elementu słownika
 * (`Position` w FK = `dictionary_items.sort_order` u nas) — ustalone w projekcie
 * rozpoznawczym `wf-symfonia-integration`. Dlatego opcje takiego poziomu pochodzą ze
 * słownika, nie z planu kont.
 */
export interface ChartDictionaryLevel {
  source: 'dictionary' | 'contractors' | 'employees';
  /** Kod lokalnego słownika do pobrania pozycji; `null` = brak powiązania (patrz niżej). */
  dictionaryCode: string | null;
  /** Nazwa poziomu z planu kont (np. „Koszty szczegółowe 2026") — do nagłówka listy. */
  name: string;
  accountNumber: string;
}

export interface AccountSegmentOption {
  /** Wartość segmentu do wstawienia w pole (np. `010` albo `1`). */
  value: string;
  /** Pełny numer konta, do którego prowadzi ten segment (prefiks + `value`). */
  accountNumber: string;
  /** Nazwa konta o tym numerze; pusta, gdy w planie istnieją tylko konta głębsze. */
  name: string;
  /** Czy na tym koncie można księgować (ostatni poziom analityki). */
  isPostable: boolean;
}

/**
 * Opcje segmentów pogrupowane po PREFIKSIE (segmenty na lewo od bieżącego, złączone `-`;
 * `''` = Syntetyka). Zbudowane raz na (firma, rok) i cache'owane przez react-query.
 */
export interface ChartSegmentIndex {
  byPrefix: Map<string, AccountSegmentOption[]>;
  /** Poziom słownikowy występujący pod danym prefiksem (klucz jak w `byPrefix`). */
  dictionaryByPrefix: Map<string, ChartDictionaryLevel>;
  /** Liczba kont, z których zbudowano indeks — do pokazania przy roku. */
  accountCount: number;
}

export const EMPTY_SEGMENT_INDEX: ChartSegmentIndex = {
  byPrefix: new Map(),
  dictionaryByPrefix: new Map(),
  accountCount: 0,
};

/**
 * Buduje indeks podpowiedzi z płaskiej listy planu kont.
 *
 * Węzły POZIOMÓW SŁOWNIKOWYCH (`dictionarySource != null`, numer z literałem `x`) NIE trafiają
 * do opcji jako wartość `x` — bo `x` nie jest niczym, co wpisuje się w dekret. Trafiają za to
 * do `dictionaryByPrefix`: pod danym prefiksem poziom jest słownikowy, a jego opcje bierze się
 * z POZYCJI elementów słownika (patrz `ChartDictionaryLevel`).
 */
export function buildSegmentIndex(rows: readonly ChartFlatRow[]): ChartSegmentIndex {
  const byPrefix = new Map<string, AccountSegmentOption[]>();
  const dictionaryByPrefix = new Map<string, ChartDictionaryLevel>();
  /** `numer konta` → wiersz, do dołożenia nazwy/księgowalności opcji kończącej się na nim. */
  const byNumber = new Map<string, ChartFlatRow>();
  for (const row of rows) {
    byNumber.set(row.accountNumber, row);
  }

  for (const row of rows) {
    if (row.dictionarySource !== null) {
      // Poziom słownikowy indeksujemy pod prefiksem = wszystko na lewo od jego WŁASNEGO,
      // NAJGŁĘBSZEGO `x`. Konto z łańcuchem słowników niesie osobny wiersz na każdy poziom
      // (`402-x`, `402-x-x`), więc `402-x` opisuje poziom pod `402`, a `402-x-x` poziom pod
      // `402-x`. Branie prefiksu po PIERWSZYM `x` wrzucałoby oba pod ten sam klucz i drugi
      // poziom przepadał (kaskada urywała się po pierwszym słowniku).
      const segs = row.accountNumber.split('-');
      const firstX = segs.indexOf(DICTIONARY_SEGMENT);
      if (firstX <= 0) continue;
      // Wymagamy CIĄGŁEGO ogona `x`-ów — numer mieszany (`402-x-1`) nie opisuje poziomu.
      if (segs.slice(firstX).some((seg) => seg !== DICTIONARY_SEGMENT)) continue;
      const prefix = segs.slice(0, segs.length - 1).join('-');
      if (!dictionaryByPrefix.has(prefix)) {
        dictionaryByPrefix.set(prefix, {
          source: row.dictionarySource,
          dictionaryCode: row.dictionaryCode ?? null,
          name: row.name,
          accountNumber: row.accountNumber,
        });
      }
      continue;
    }
    const segments = row.accountNumber.split('-');
    if (segments.some((seg) => seg === DICTIONARY_SEGMENT)) continue;

    for (let depth = 0; depth < segments.length; depth++) {
      const prefix = segments.slice(0, depth).join('-');
      const value = segments[depth]!;
      if (!value) continue;
      const bucket = byPrefix.get(prefix) ?? [];
      if (bucket.some((opt) => opt.value === value)) continue;

      const accountNumber = prefix ? `${prefix}-${value}` : value;
      const exact = byNumber.get(accountNumber);
      bucket.push({
        value,
        accountNumber,
        name: exact?.name ?? '',
        isPostable: exact?.isPostable ?? false,
      });
      byPrefix.set(prefix, bucket);
    }
  }

  // Sortowanie numeryczne per segment (`600-2` przed `600-10`) — ten sam powód co przy
  // sortowaniu wyniku importu planu kont.
  for (const bucket of byPrefix.values()) {
    bucket.sort((a, b) => a.value.localeCompare(b.value, undefined, { numeric: true }));
  }

  return { byPrefix, dictionaryByPrefix, accountCount: rows.length };
}

/**
 * Zamienia PREFIKS WPISANY PRZEZ USERA na ścieżkę w planie kont.
 *
 * Sedno problemu: plan kont trzyma poziom słownikowy jako `x` (`402-x`), a user wpisuje w tym
 * miejscu KONKRETNĄ pozycję elementu słownika (`402-101`). Bez tego przeliczenia kaskada
 * urywałaby się na pierwszym poziomie słownikowym — `402-101` nie istnieje w planie, więc dla
 * kolejnych poziomów nie znaleźlibyśmy nic.
 *
 * Zwraca `null`, gdy któryś wpisany segment jest pusty albo nie pasuje ani do konta z planu,
 * ani do poziomu słownikowego — wtedy świadomie nie podpowiadamy głębiej (user pisze coś
 * spoza planu, a zgadywanie gałęzi wprowadzałoby w błąd).
 */
export function resolvePlanPrefix(
  index: ChartSegmentIndex,
  typedSegments: readonly string[],
): string | null {
  const path: string[] = [];
  for (const raw of typedSegments) {
    const seg = raw.trim();
    if (!seg) return null;
    const key = path.join('-');
    const literal = index.byPrefix.get(key)?.some((opt) => opt.value === seg) ?? false;
    if (literal) {
      path.push(seg);
    } else if (index.dictionaryByPrefix.has(key)) {
      path.push(DICTIONARY_SEGMENT);
    } else {
      return null;
    }
  }
  return path.join('-');
}

/** Skąd brać opcje danego poziomu: z planu kont, ze słownika, albo znikąd. */
export type SegmentSource =
  | { kind: 'accounts'; options: AccountSegmentOption[] }
  | { kind: 'dictionary'; level: ChartDictionaryLevel }
  | { kind: 'none' };

/**
 * Rozstrzyga źródło opcji dla segmentu o podanym (WPISANYM) prefiksie. Konta z planu mają
 * pierwszeństwo — poziom słownikowy zwracamy tylko wtedy, gdy pod tym prefiksem plan nie ma
 * zwykłych kont (w praktyce te dwa przypadki się wykluczają).
 */
export function segmentSource(
  index: ChartSegmentIndex,
  typedPrefix: readonly string[],
): SegmentSource {
  const planPrefix = typedPrefix.length === 0 ? '' : resolvePlanPrefix(index, typedPrefix);
  if (planPrefix === null) return { kind: 'none' };

  const options = index.byPrefix.get(planPrefix);
  if (options && options.length > 0) return { kind: 'accounts', options };

  const level = index.dictionaryByPrefix.get(planPrefix);
  if (level) return { kind: 'dictionary', level };

  return { kind: 'none' };
}

/**
 * Opcje KONT dla segmentu o podanym prefiksie. Cieńszy wariant `segmentSource` — zwraca
 * pustą listę także wtedy, gdy poziom jest słownikowy (jego opcje pochodzą ze słownika,
 * nie z planu kont).
 */
export function segmentOptions(
  index: ChartSegmentIndex,
  prefixSegments: readonly string[],
): AccountSegmentOption[] {
  const source = segmentSource(index, prefixSegments);
  return source.kind === 'accounts' ? source.options : [];
}

/** Filtr listy po tym, co user już wpisał w polu (po wartości segmentu ORAZ po nazwie). */
export function filterSegmentOptions(
  options: readonly AccountSegmentOption[],
  query: string,
): AccountSegmentOption[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...options];
  return options.filter(
    (opt) => opt.value.toLowerCase().startsWith(q) || opt.name.toLowerCase().includes(q),
  );
}

// ─── Hooki (dane referencyjne, tylko odczyt) ─────────────────────────────────

/** `403`/`402` traktujemy jak „brak planu kont" — podpowiedzi są opcjonalne, nie mogą
 * wywalić formatki dekretacji userowi bez uprawnienia do planu kont ani firmie bez modułu. */
function isMissingAccessError(err: unknown): boolean {
  return isAxiosError(err) && (err.response?.status === 403 || err.response?.status === 402);
}

export function useChartFiscalYears(companyId: string | null | undefined) {
  return useQuery({
    queryKey: ['chart-of-accounts', 'years', companyId],
    queryFn: async (): Promise<ChartFiscalYear[]> => {
      try {
        const res = await api.get<{ years: ChartFiscalYear[] }>(
          `/chart-of-accounts/years?companyId=${companyId}`,
        );
        return res.data.years;
      } catch (err) {
        if (isMissingAccessError(err)) return [];
        throw err;
      }
    },
    enabled: !!companyId,
    retry: false,
  });
}

export function useChartSegmentIndex(
  companyId: string | null | undefined,
  fiscalYear: string | null,
) {
  return useQuery({
    queryKey: ['chart-of-accounts', 'segments', companyId, fiscalYear],
    queryFn: async (): Promise<ChartSegmentIndex> => {
      try {
        const params = new URLSearchParams({ companyId: String(companyId), active: 'true' });
        if (fiscalYear) params.set('fiscalYear', fiscalYear);
        const res = await api.get<{ accounts: ChartFlatRow[] }>(
          `/chart-of-accounts?${params.toString()}`,
        );
        return buildSegmentIndex(res.data.accounts ?? []);
      } catch (err) {
        if (isMissingAccessError(err)) return EMPTY_SEGMENT_INDEX;
        throw err;
      }
    },
    enabled: !!companyId && !!fiscalYear,
    retry: false,
    // Plan kont zmienia się rzadko (import per rok), a lista bywa tysiącami wierszy —
    // nie przeładowujemy jej przy każdym powrocie na zakładkę.
    staleTime: 5 * 60 * 1000,
  });
}

/** Kontrahent w kartotece Symfonii — pozycja jest wartością wpisywaną w segment konta. */
export interface SymfoniaContractorPosition {
  position: number;
  contractorId: string;
  name: string;
  taxId: string | null;
}

/**
 * Pozycje kontrahentów w kartotece Symfonii dla firmy — podpowiedzi i podpis dla poziomu
 * analityki oznaczonego w planie kont jako KARTOTEKA KONTRAHENTÓW (`dictionary_source`
 * `contractors`). Taki poziom nie ma kodu słownika, więc `useDictionary` go NIE obsłuży —
 * to była przyczyna pustej listy i braku podpisu (wpisywanie numeru na ślepo).
 *
 * `402`/`403` → pusta lista tą samą zasadą co plan kont: podpowiedzi są opcjonalne i nie mogą
 * wywalić formatki tenantowi bez modułu Symfonii ani userowi bez wglądu w kontrahentów.
 */
export function useSymfoniaContractorPositions(companyId: string | null | undefined) {
  return useQuery({
    queryKey: ['contractors', 'symfonia-positions', companyId],
    queryFn: async (): Promise<SymfoniaContractorPosition[]> => {
      try {
        const res = await api.get<{ items: SymfoniaContractorPosition[] }>(
          `/contractors/symfonia-positions?companyId=${companyId}`,
        );
        return res.data.items ?? [];
      } catch (err) {
        if (isMissingAccessError(err)) return [];
        throw err;
      }
    },
    enabled: !!companyId,
    retry: false,
    // Kartoteka rośnie synchronizacją, nie w trakcie dekretowania jednego dokumentu.
    staleTime: 5 * 60 * 1000,
  });
}
